<template>
  <div class="groups-page">
    <AlertBar ref="alertBar" />

    <UploadGroupModal
      @updateGroupsList="updateGroupsList"
      @alert="handleAlert"
      ref="modalGroupUpload"
    />

    <v-card class="card" v-if="!importedPageLayout">
      <div class="header">
        <div class="header-title" data-test-header-title>
          <h1>{{ $t('GroupTab.title') }}</h1>
          <HowToBeginAndWatchAVideo videoType="listGroups" short />
        </div>
        <div class="header-subtitle">
          <h3>{{ $t('GroupTab.subtitle') }}</h3>
        </div>
      </div>

      <v-divider></v-divider>

      <div class="table-header">
        <span class="table-header-title">
          {{ $t('GroupTab.tableHeaderTitle') }}
        </span>

        <v-spacer />

        <ItemSelector
          ref="progressType"
          :menuConfig="{
            showTabs: false,
          }"
          :inputConfig="{
            closeOnSelect: true,
            showAvatar: false,
          }"
          :menuOptions="_statusOptions"
          @update:item="handleStatus($event)"
        >
          <template #default="{ focus, selectedItem }">
            <div class="filter-status" v-ripple @click="focus">
              <div class="container">
                <div class="label">
                  {{
                    selectedItem && selectedItem.data.length
                      ? selectedItem.data[0].label
                      : $t('GroupTab.status.title')
                  }}
                </div>

                <v-icon class="droparrow" right size="0.625rem">
                  fi fi-br-caret-down
                </v-icon>
              </div>
            </div>
          </template>
        </ItemSelector>
      </div>

      <!--
        Alerta de imports simultâneos (plano híbrido §8/§3.3.4, aceite F4.v):
        um import de grupos legado e um v2 pendentes ao mesmo tempo podem
        divergir no upsert (costCenter × groupID) — avisar o operador.
      -->
      <div
        v-if="_hasSimultaneousPendingFlows"
        class="simultaneous-flows-alert"
        data-test-simultaneous-flows-alert
      >
        <v-icon size="14">fi fi-rr-triangle-warning</v-icon>
        <span>{{ $t('GroupTab.simultaneousFlowsAlert') }}</span>
      </div>

      <div v-if="loading">
        <v-skeleton-loader type="table-tbody" data-test-ranking-loading />
      </div>

      <div
        v-else
        class="table-holder"
        data-test-row-click-area
        @click="handleRowClick"
      >
        <DataTable
          id="DataTable"
          :title="$t('GroupTab.tableHeaderTitle')"
          :headers="_headers"
          :hideHeader="true"
          :hideOption="true"
          :hideSearch="true"
          :contentStyle="false"
          :enablePagination="false"
          :items="groups"
          :itemsPerPage="pagination.limit"
          :skeletonLines="pagination.limit"
          @send="handleSend($event)"
          @search="handleSearch($event)"
        >
        </DataTable>

        <div class="pagination">
          <v-btn
            data-test-table-pagination-previous
            v-if="pagination.page > 1"
            x-small
            tile
            icon
            @click="handlePagination(pagination.page - 1)"
          >
            <v-icon size="10">fi fi-br-angle-left</v-icon>
          </v-btn>

          <div class="current">
            <span class="label-pagination"> {{ pagination.page }} </span>
          </div>

          <v-btn
            data-test-table-pagination-next
            v-if="pagination.page < pagination.total"
            x-small
            tile
            icon
            @click="handlePagination(pagination.page + 1)"
          >
            <v-icon size="10">fi fi-br-angle-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>

    <router-view @alert="handleAlert($event)" />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { getProcessedGroups } from '@/services/groups'
import {
  SYNC_FLOWS,
  GROUPS_UPLOAD_OPTIONS,
  findUploadOptionLabelKey,
} from '@/helpers/upload-flows'
import { resetPaginationToFirstPage } from '@/helpers/listing-table'
import { listingTableMixin } from '@/mixins/listing-table'
import { listingFetchErrorMixin } from '@/mixins/listing-fetch-error'
import UploadGroupModal from '@/views/SyncPage/Tabs/DataPage/Tabs/GroupsPage/Partials/UploadGroupModal/UploadGroupModal.vue'

export default {
  name: 'GroupsPage',

  components: {
    UploadGroupModal,
  },

  mixins: [listingTableMixin, listingFetchErrorMixin],

  data() {
    return {
      loading: true,
      pagination: {
        page: 1,
        limit: 20,
        offset: 0,
        total: 0,
      },
      filters: {},
      groups: [],
    }
  },

  created() {
    this.setImportedPageLayout(!!this.$route.params.id)
    this.$root.$on('modalGroupUpload', this.handleUploadModal)
  },

  mounted() {
    this.setCurrentPage('GroupsPage')
    this.setImportedPageLayout(!!this.$route.params.id)
    this.getProcessedGroups()
  },

  watch: {
    importedPageLayout(val) {
      if (!val) this.filters = {}

      this.getProcessedGroups()
    },
  },

  computed: {
    ...mapGetters(['importedPageLayout']),

    // Alerta F4.v (plano §8): detecta, na página corrente da lista, imports
    // pendentes (validating/waiting/importing) nos DOIS fluxos ao mesmo
    // tempo. Best-effort — a lista é paginada/filtrável; a detecção cobre o
    // que o operador está vendo.
    _hasSimultaneousPendingFlows() {
      const PENDING_STATUSES = ['validating', 'waiting', 'importing']

      const pendingFlows = new Set(
        this.groups
          .filter(el => PENDING_STATUSES.includes(el.rawStatus))
          .map(el => el.flow || SYNC_FLOWS.Legacy)
      )

      return (
        pendingFlows.has(SYNC_FLOWS.Legacy) && pendingFlows.has(SYNC_FLOWS.V2)
      )
    },

    _statusOptions() {
      return [
        {
          value: 'status',
          label: this.$t('common.status'),
          type: 'listview',
          items: [
            {
              id: 'validating',
              label: this.$t('GroupTab.status.validating'),
            },
            {
              id: 'waiting',
              label: this.$t('GroupTab.status.waiting'),
            },
            {
              id: 'rejected',
              label: this.$t('GroupTab.status.rejected'),
            },
            {
              id: 'importing',
              label: this.$t('GroupTab.status.importing'),
            },
            {
              id: 'imported',
              label: this.$t('GroupTab.status.imported'),
            },
            {
              id: 'error',
              label: this.$t('GroupTab.status.error'),
            },
          ],
        },
      ]
    },

    // Item 5 — alinhamento: todas as colunas centralizadas, exceto a do
    // nome do arquivo (align: 'start'). O align vale para o header
    // (v-data-table default header do Vuetify); o corpo é centralizado por
    // CSS scoped (::v-deep) no style.scss compartilhado com a PeoplePage.
    _headers() {
      return [
        {
          text: this.$t('PeopleService.importedFilesTable.headers.filename'),
          value: 'filename',
          type: 'entity-item',
          align: 'start',
          sortable: true,
        },
        {
          text: this.$t('PeopleService.importedFilesTable.headers.date'),
          value: 'date',
          type: 'text',
          align: 'center',
          sortable: true,
        },
        // Item 5 — opção de importação selecionada no modal (mesmo rótulo
        // do radio). Sem sort: rótulo é derivado, o backend não ordena
        {
          text: this.$t('PeopleService.importedFilesTable.headers.action'),
          value: 'actionLabel',
          type: 'text',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t(
            'PeopleService.importedFilesTable.headers.peopleAmount'
          ),
          value: 'groupAmount',
          type: 'text',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t(
            'PeopleService.importedFilesTable.headers.amountSuccess'
          ),
          value: 'amountSuccess',
          type: 'text',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t('PeopleService.importedFilesTable.headers.amountError'),
          value: 'amountError',
          type: 'text',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t('PeopleService.importedFilesTable.headers.amountAlert'),
          value: 'amountAlert',
          type: 'text',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t('PeopleService.importedFilesTable.headers.status'),
          value: 'status',
          type: 'text-status',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t('PeopleService.importedFilesTable.headers.actions'),
          value: 'actions',
          type: 'action-buttons',
          width: '100',
          align: 'center',
          sortable: false,
        },
      ]
    },
  },

  beforeDestroy() {
    this.$root.$off('modalGroupUpload', this.handleUploadModal)
  },

  methods: {
    ...mapActions(['setImportedPageLayout', 'setCurrentPage']),

    async getProcessedGroups() {
      this.loading = true

      const payload = {
        limit: this.pagination.limit,
        offset: this.pagination.offset,
        ...this.filters,
      }

      await getProcessedGroups(payload)
        .then(({ data, headers }) => {
          this.groups = data.map(el => {
            return {
              ...el,
              filename: { title: el.filename },
              date: this.formatDate(el.date),
              actionLabel: this.mapActionLabel(el),
              // status cru preservado para o alerta de fluxos simultâneos
              // (o campo status vira objeto de exibição logo abaixo)
              rawStatus: el.status,
              status: {
                label: this.$t(`GroupTab.status.${el.status}`),
                status: this.formatStatus(el.status),
                tooltip: el.importStatusMessage || '',
              },
              actions: ['send'],
            }
          })

          let total = headers['x-count'] / this.pagination.limit
          if (total % 1 !== 0) total = total + 1

          this.pagination.total = Math.trunc(total)
        })
        .catch(error =>
          this.notifyListingFetchError(error, 'getProcessedGroups')
        )
        .finally(() => {
          this.loading = false
        })
    },

    updateGroupsList() {
      this.getProcessedGroups()
    },

    // Contrato do listingTableMixin (handleRowClick/mapActionLabel)
    listingItems() {
      return this.groups
    },
    // Item 5 — { action, flow } → labelKey do radio do modal; arquivo sem
    // flow (pré-híbrido) = legado
    actionLabelKey(file) {
      return findUploadOptionLabelKey(GROUPS_UPLOAD_OPTIONS, file)
    },

    handlePagination(value) {
      this.pagination.page = value
      this.pagination.offset =
        (this.pagination.page - 1) * this.pagination.limit

      this.getProcessedGroups()
    },

    handleAlert(event) {
      const config = {
        messagePrefix: '',
        description: event.message,
        type: event.type,
        hasLeftBorder: false,
        hasFooter: false,
      }

      this.$refs.alertBar.displayAlert(config)
    },

    handleUploadModal() {
      this.$nextTick(() => {
        this.$refs.modalGroupUpload?.handleDialog()
      })
    },

    handleSend(event) {
      this.$router.push({
        name: 'ImportedGroupsPage',
        params: { id: event.id },
      })
    },

    // Item 4 — clique na linha abre o detalhe: listingTableMixin
    // (handleRowClick com guards de elemento interativo e drag de seleção)

    handleSort({ sortBy: sortField, sortDesc }) {
      const payload = {
        sortBy: sortField.length ? sortField[0] : null,
        sortOrder: sortField.length ? (sortDesc[0] ? 'desc' : 'asc') : null,
      }

      this.filters = {
        ...this.filters,
        ...payload,
      }

      this.getProcessedGroups()
    },

    handleSearch(search) {
      const payload = {
        filename: search.name !== '' ? search.name : null,
      }

      this.filters = {
        ...this.filters,
        ...payload,
      }

      this.getProcessedGroups()
    },

    handleStatus(event) {
      const payload = {
        status: event ? event.id : null,
      }

      this.filters = {
        ...this.filters,
        ...payload,
      }

      resetPaginationToFirstPage(this.pagination)

      this.getProcessedGroups()
    },

    formatStatus(value) {
      const data = {
        validating: 'waiting',
        waiting: 'waiting',
        rejected: 'error',
        importing: 'waiting',
        imported: 'success',
        error: 'error',
      }

      return data[value]
    },

    formatDate(value) {
      let newDate = new Date(value)

      return `${newDate.toLocaleDateString()} ${newDate.toLocaleTimeString()}`
    },
  },
}
</script>

<style
  lang="scss"
  scoped
  src="@/views/SyncPage/Tabs/DataPage/Tabs/PeoplePage/style.scss"
/>
